import type { ComponentType, ReactNode } from 'react';
import { Route, Navigate, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import type { RouteConfig } from '@/config/routes.config';

/**
 * A redirect that keeps the query string and hash.
 *
 * `<Navigate to="/announcements" />` with a plain string DISCARDS them, so a
 * link somebody was sent — /settings/announcements?announcementId=abc — would
 * land on the list instead of the announcement it named. Every route we move
 * has to keep its deep links working, or moving one quietly breaks the emails
 * that already went out.
 */
function RedirectTo({ to }: { to: string }) {
  const { search, hash } = useLocation();
  // A redirect target that names its own query wins; otherwise carry the
  // caller's through.
  const [pathname, ownSearch] = to.split('?');
  return (
    <Navigate to={{ pathname, search: ownSearch ? `?${ownSearch}` : search, hash }} replace />
  );
}

export function renderRoutes(
  routes: RouteConfig[],
  options: { layoutOverride?: ComponentType } = {},
): JSX.Element[] {
  return routes.map((route, i) => {
    if (route.redirect) {
      return <Route key={i} path={route.path} element={<RedirectTo to={route.redirect} />} />;
    }

    // The first top-level route supports layout override (for embedded mode)
    const Layout = i === 0 && options.layoutOverride ? options.layoutOverride : route.layout;

    let element: ReactNode;
    if (Layout) {
      element = <Layout />;
    } else if (route.component) {
      const Component = route.component;
      element = route.requiredRole
        ? <ProtectedRoute requiredRole={route.requiredRole}><Component /></ProtectedRoute>
        : <Component />;
    }

    return (
      <Route key={i} path={route.path} index={route.index} element={element}>
        {route.children && renderRoutes(route.children)}
      </Route>
    );
  });
}
